import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from "react";
import api from "@/api";

/**
 * Context per la password area riservata Contabilità
 * (Costi Fissi, Costi Variabili, Cash Flow, BEP, Situazione Economica, Report ore).
 *
 * Persistenza: sessionStorage (sopravvive al reload della pagina ma NON alla
 * chiusura della tab / logout). Auto-lock dopo 60 minuti di inattività.
 */
const FinanceAuthContext = createContext(null);

const AUTO_LOCK_MS = 60 * 60 * 1000; // 60 min
const STORAGE_KEY = "ethos.finance.unlock";

function _readStored() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return "";
    const { pw, exp } = JSON.parse(raw);
    if (!pw || !exp || Date.now() > exp) {
      sessionStorage.removeItem(STORAGE_KEY);
      return "";
    }
    return pw;
  } catch {
    return "";
  }
}

function _persist(pw) {
  try {
    if (pw) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ pw, exp: Date.now() + AUTO_LOCK_MS }));
    } else {
      sessionStorage.removeItem(STORAGE_KEY);
    }
  } catch { /* silent */ }
}

export function FinanceAuthProvider({ children }) {
  const [pw, setPwState] = useState(() => _readStored());
  const timerRef = useRef(null);

  const clear = useCallback(() => {
    setPwState("");
    _persist("");
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const setPw = useCallback((next) => {
    setPwState(next || "");
    _persist(next || "");
    if (timerRef.current) clearTimeout(timerRef.current);
    if (next) {
      timerRef.current = setTimeout(() => {
        setPwState("");
        _persist("");
      }, AUTO_LOCK_MS);
    }
  }, []);

  const verifyAndSet = useCallback(async (candidate) => {
    // Chiama l'endpoint di verify e, se ok, salva la password in memoria + sessionStorage
    const { data } = await api.post("/admin/situazione-economica/verify", { password: candidate });
    setPw(candidate);
    return data;
  }, [setPw]);

  // avvia il timer se abbiamo caricato una password già valida dallo storage
  useEffect(() => {
    if (pw && !timerRef.current) {
      timerRef.current = setTimeout(() => {
        setPwState("");
        _persist("");
      }, AUTO_LOCK_MS);
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = { pw, unlocked: Boolean(pw), setPw, clear, verifyAndSet };
  return <FinanceAuthContext.Provider value={value}>{children}</FinanceAuthContext.Provider>;
}

export function useFinanceAuth() {
  const ctx = useContext(FinanceAuthContext);
  if (!ctx) throw new Error("useFinanceAuth must be used inside FinanceAuthProvider");
  return ctx;
}
